import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { ArrowRight, MapPin, Award, CheckCircle2 } from "lucide-react";

export default function DoctorProfile() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [doc, setDoc] = useState(null);
  const submissionId = searchParams.get("submissionId") || "";

  useEffect(() => {
    api.get(`/doctors/${slug}`).then(r => setDoc(r.data)).catch(() => setDoc(false));
  }, [slug]);

  if (doc === null) return <div className="sv-doc-profile"><div className="container-x"><div className="spinner" /></div></div>;
  if (doc === false) return <div className="sv-doc-profile"><div className="container-x"><p>Doctor not found.</p></div></div>;

  return (
    <div data-testid="doctor-profile-page">
      <Nav theme="light" />
      <div className="sv-doc-profile">
        <div className="container-x">
          <Link to="/doctors" style={{ fontSize: 13, color: "var(--ink-60)", fontFamily: "var(--font-mono)", letterSpacing: "0.1em", textTransform: "uppercase" }}>← Back to directory</Link>

          <div className="sv-doc-profile-grid" style={{ marginTop: 24 }}>
            <div className="main">
              <div style={{ display: "flex", alignItems: "flex-start", gap: 24 }}>
                <div className="sv-doc-avatar" style={{ width: 84, height: 84, fontSize: 22 }}>{doc.avatar_initials}</div>
                <div>
                  <div className="specialty">{doc.specialty}</div>
                  <h1>{doc.name}</h1>
                  <div style={{ fontSize: 14, color: "var(--ink-60)", marginTop: 6 }}>{doc.qualifications}</div>
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 20 }}>
                <span className="badge" style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", padding: "6px 12px", borderRadius: 999, background: "rgba(78,205,196,0.15)", color: "var(--teal)", fontWeight: 500 }}>
                  <CheckCircle2 size={11} style={{ display: "inline", marginRight: 4 }} /> NMC Verified · {doc.nmc_number}
                </span>
                {doc.telemedicine && <span className="badge" style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", padding: "6px 12px", borderRadius: 999, background: "rgba(78,205,196,0.15)", color: "var(--teal)", fontWeight: 500 }}>Telemedicine available</span>}
                {doc.accepting_new && <span className="badge" style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", padding: "6px 12px", borderRadius: 999, background: "#D1F0E6", color: "#0F6E56", fontWeight: 500 }}>Accepting new patients</span>}
              </div>

              <p className="bio" data-testid="doctor-bio">{doc.bio}</p>

              <div className="meta-grid">
                <div className="meta-item">
                  <div className="k">Experience</div>
                  <div className="v">{doc.experience_years}+ years</div>
                </div>
                <div className="meta-item">
                  <div className="k">Languages</div>
                  <div className="v">{doc.languages.join(" · ")}</div>
                </div>
                <div className="meta-item">
                  <div className="k">Hospital</div>
                  <div className="v">{doc.hospital}</div>
                </div>
                <div className="meta-item">
                  <div className="k">Location</div>
                  <div className="v">{doc.address}</div>
                </div>
                <div className="meta-item">
                  <div className="k">In-person fee</div>
                  <div className="v">₹{doc.fee_in_person.toLocaleString()}</div>
                </div>
                <div className="meta-item">
                  <div className="k">Telemedicine fee</div>
                  <div className="v">₹{doc.fee_telemedicine.toLocaleString()}</div>
                </div>
              </div>

              {doc.focus_areas?.length > 0 && (
                <div style={{ marginTop: 28 }}>
                  <span className="eyebrow eyebrow-navy">Focus areas</span>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                    {doc.focus_areas.map((f, i) => (
                      <span key={f} style={{ padding: "8px 14px", background: "var(--cream)", borderRadius: 999, fontSize: 13, color: "var(--navy)" }}>{f}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="sidebar">
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--mint)" }}>
                Ready to book?
              </div>
              <h3 style={{ fontFamily: "var(--font-display)", fontSize: 24, margin: "10px 0 18px", fontWeight: 400 }}>Book a sleep study with {doc.name.split(" ").slice(0, 2).join(" ")}.</h3>
              <p style={{ fontSize: 14, color: "rgba(255,255,255,0.7)", lineHeight: 1.55, marginBottom: 24 }}>
                You&apos;ll pick home or in-clinic, share your details, and the doctor&apos;s team will confirm your slot within 24 hours.
              </p>
              <Link
                to={`/book?doctor=${doc.id}${submissionId ? `&submissionId=${submissionId}` : ""}`}
                className="btn btn-primary"
                style={{ width: "100%", justifyContent: "center" }}
                data-testid="book-with-doctor-cta"
              >
                Book with {doc.name.split(" ")[0]} {doc.name.split(" ")[1]} <ArrowRight size={16} />
              </Link>
              <div style={{ marginTop: 28, paddingTop: 24, borderTop: "1px solid rgba(255,255,255,0.1)", fontSize: 12, color: "rgba(255,255,255,0.5)", fontFamily: "var(--font-mono)", letterSpacing: "0.1em", lineHeight: 1.6 }}>
                Somveil charges nothing for browsing. Booking fees are collected by the clinic or lab directly.
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
